import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabase";
import { TabsBar } from "../components/TabsBar";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ScheduleRow = {
  id: number;
  route_id: number;
  trip_no: number;
  departure_time: string | null;
  prayer_anchor: string | null;
  prayer_offset_min: number;
  days_mask: number;
  schedule_set: string;
  routes: { name: string } | null;
};

type CancellationRow = {
  id: number;
  schedule_id: number;
  date: string;
  reason: string | null;
  schedules: {
    trip_no: number;
    departure_time: string | null;
    prayer_anchor: string | null;
    prayer_offset_min: number;
    routes: { name: string } | null;
  } | null;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const todayIso = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const todayMask = (): number => {
  const day = new Date().getDay(); // 0=Sun, 1=Mon…
  const map = [64, 1, 2, 4, 8, 16, 32];
  return map[day];
};

function formatDeparture(
  s: {
    departure_time: string | null;
    prayer_anchor: string | null;
    prayer_offset_min: number;
  },
): string {
  if (s.departure_time) return s.departure_time.slice(0, 5);
  if (s.prayer_anchor) {
    const offset =
      s.prayer_offset_min > 0 ? ` +${s.prayer_offset_min}m` : "";
    return `After ${s.prayer_anchor}${offset}`;
  }
  return "—";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AnnouncementsPage() {
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [cancellations, setCancellations] = useState<CancellationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(
    null,
  );
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [schedRes, cancelRes] = await Promise.all([
        supabase
          .from("schedules")
          .select(
            "id, route_id, trip_no, departure_time, prayer_anchor, prayer_offset_min, days_mask, schedule_set, routes(name)",
          )
          .eq("suspended", false)
          .order("trip_no", { ascending: true }),
        supabase
          .from("trip_cancellations")
          .select(
            "id, schedule_id, date, reason, schedules(trip_no, departure_time, prayer_anchor, prayer_offset_min, routes(name))",
          )
          .eq("date", todayIso())
          .order("id", { ascending: false }),
      ]);
      if (schedRes.error) throw schedRes.error;
      if (cancelRes.error) throw cancelRes.error;
      setSchedules((schedRes.data ?? []) as unknown as ScheduleRow[]);
      setCancellations((cancelRes.data ?? []) as unknown as CancellationRow[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const todaysMask = todayMask();
  const todaysSchedules = useMemo(
    () => schedules.filter((s) => (s.days_mask & todaysMask) !== 0),
    [schedules, todaysMask],
  );

  const cancelTrip = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedScheduleId) {
      setError("Select a trip to cancel.");
      return;
    }
    setSubmitting(true);
    setError(null);
    const { error: insErr } = await supabase
      .from("trip_cancellations")
      .insert({
        schedule_id: selectedScheduleId,
        date: todayIso(),
        reason: reason.trim() || null,
      });
    setSubmitting(false);
    if (insErr) {
      setError(insErr.message);
      return;
    }
    setSelectedScheduleId(null);
    setReason("");
    void refresh();
  };

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.h1}>Trip cancellations</h1>
          <p style={styles.muted}>
            Cancel today&apos;s trips and view today&apos;s cancellations.
          </p>
        </div>
        <div style={styles.headerRight}>
          <Link to="/dashboard" style={styles.linkButton}>
            ← Dashboard
          </Link>
        </div>
      </header>

      <TabsBar active="dashboard" />

      {error && (
        <div
          style={{ ...styles.alert, background: "#fdecea", color: "#a02622" }}
        >
          {error}
        </div>
      )}

      <section style={styles.card}>
        <h2 style={styles.h2}>Cancel a trip today</h2>
        <form onSubmit={cancelTrip} style={styles.form}>
          <label style={styles.label}>
            Trip
            <select
              value={selectedScheduleId ?? ""}
              onChange={(e) =>
                setSelectedScheduleId(
                  e.target.value ? Number(e.target.value) : null,
                )
              }
              style={styles.input}
              required
            >
              <option value="">Select a trip…</option>
              {todaysSchedules.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.routes?.name ?? `Route ${s.route_id}`} — Trip {s.trip_no}{" "}
                  — {formatDeparture(s)}
                </option>
              ))}
            </select>
          </label>
          <label style={styles.label}>
            Reason (optional)
            <input
              type="text"
              placeholder="e.g. Vehicle breakdown"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={styles.input}
            />
          </label>
          <div style={styles.formActions}>
            <button
              type="submit"
              disabled={submitting || !selectedScheduleId}
              style={styles.button}
            >
              {submitting ? "Cancelling…" : "Cancel trip"}
            </button>
          </div>
        </form>
      </section>

      <section style={styles.card}>
        <h2 style={styles.h2}>Today&apos;s cancellations ({todayIso()})</h2>
        {loading ? (
          <p style={styles.muted}>Loading…</p>
        ) : cancellations.length === 0 ? (
          <p style={styles.muted}>No cancellations today.</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Trip</th>
                <th style={styles.th}>Departure</th>
                <th style={styles.th}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {cancellations.map((c) => (
                <tr key={c.id}>
                  <td style={styles.td}>
                    {c.schedules?.routes?.name ?? "—"} — Trip{" "}
                    {c.schedules?.trip_no ?? "—"}
                  </td>
                  <td style={styles.td}>
                    {c.schedules ? formatDeparture(c.schedules) : "—"}
                  </td>
                  <td style={styles.td}>{c.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "system-ui, -apple-system, sans-serif",
    maxWidth: 960,
    margin: "0 auto",
    padding: 24,
    color: "#1a1a1a",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
  },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  h1: { margin: 0, fontSize: 20 },
  h2: { margin: 0, fontSize: 16 },
  muted: { color: "#666", fontSize: 14, margin: 0 },
  alert: {
    padding: "10px 14px",
    border: "1px solid #f0c674",
    borderRadius: 6,
    marginBottom: 16,
    fontWeight: 600,
  },
  card: {
    padding: 20,
    border: "1px solid #ddd",
    borderRadius: 8,
    background: "#fff",
    marginBottom: 20,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  formActions: { display: "flex", justifyContent: "flex-end" },
  label: { display: "flex", flexDirection: "column", gap: 4, fontSize: 14 },
  input: {
    padding: "8px 10px",
    border: "1px solid #ccc",
    borderRadius: 4,
    fontSize: 14,
  },
  button: {
    padding: "10px 14px",
    background: "#1a3a5c",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 14,
  },
  linkButton: {
    padding: "6px 10px",
    background: "#f1f1f1",
    color: "#1a1a1a",
    border: "1px solid #ccc",
    borderRadius: 4,
    textDecoration: "none",
    fontSize: 13,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "2px solid #1a3a5c",
  },
  td: { padding: "8px 10px", borderBottom: "1px solid #eee" },
};
